'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { useRouter } from '@/i18n/routing';
import { ApplicationDialog, LANDING_ASSETS, LandingCta, LandingFaq } from '../_components';
import { useArtistProgramStatus } from '../_hooks/use-artist-program-status';

const BENEFIT_KEYS = [
  { index: '01', title: 'benefit1Title', body: 'benefit1Body' },
  { index: '02', title: 'benefit2Title', body: 'benefit2Body' },
  { index: '03', title: 'benefit3Title', body: 'benefit3Body' },
  { index: '04', title: 'benefit4Title', body: 'benefit4Body' },
  { index: '05', title: 'benefit5Title', body: 'benefit5Body' },
] as const;

const CRITERIA_KEYS = ['criteria1', 'criteria2', 'criteria3', 'criteria4', 'criteria5'] as const;

const STEP_KEYS = [
  { step: 'step1Label', title: 'step1Title', body: 'step1Body', image: LANDING_ASSETS.step1 },
  { step: 'step2Label', title: 'step2Title', body: 'step2Body', image: LANDING_ASSETS.step2 },
  { step: 'step3Label', title: 'step3Title', body: 'step3Body', image: LANDING_ASSETS.step3 },
] as const;

type ApplicationDialogView = 'application' | 'reviewing' | 'rejected' | null;

export default function ArtistProgramLandingPage() {
  const t = useTranslations('artistProgram.landing');
  const router = useRouter();
  const user = useStore((state) => state.userInfo);
  const { open } = useGlobalModal('loginDialog');
  const { refresh, status, my } = useArtistProgramStatus();
  const [dialogView, setDialogView] = useState<ApplicationDialogView>(null);

  useEffect(() => {
    if (user && !status) void refresh().catch(() => undefined);
  }, [refresh, status, user]);

  const handleApply = () => {
    if (!user) {
      open({ redirectUrl: '/artist-program' });
      return;
    }
    if (status === 'approved') {
      router.push('/artist-program/activities');
      return;
    }
    if (status === 'reviewing' || status === 'rejected') {
      setDialogView(status);
      return;
    }
    setDialogView('application');
  };

  const ctaLabel = status === 'approved' ? t('viewActivities') : t('apply');

  return (
    <>
      <section className="relative flex min-h-[900px] items-center overflow-hidden sm:min-h-[640px]">
        <img
          src={LANDING_ASSETS.heroBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative z-10 mx-auto flex w-full max-w-[1680px] flex-col items-center px-12 py-[120px] text-center sm:px-4 sm:py-24">
          {SITE === 'china' && (
            <div className="rounded-8 bg-white/12 px-3 py-1.5 font-medium text-sm text-white/90">{t('badge')}</div>
          )}
          <h1 className="mt-5 font-semibold text-[52px] leading-[1.15] sm:mt-4 sm:text-4xl sm:leading-tight">
            {t.rich('heroTitle', {
              br: () => <br />,
              gradient: (chunks) => (
                <span
                  className="inline-block bg-gradient-to-r from-[#587ffa] to-[#ffb693] bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(130deg, #587ffa 10.89%, #ffb693 117.82%)' }}
                >
                  {chunks}
                </span>
              ),
            })}
          </h1>
          <p className="mt-5 max-w-[690px] text-center font-normal text-base text-white/72 leading-[1.6] sm:mt-4 sm:text-sm">
            {t('subtitle')}
          </p>
          <LandingCta className="mt-7" onClick={handleApply}>
            {ctaLabel}
          </LandingCta>
        </div>
      </section>

      <section className="relative overflow-hidden px-24 py-[120px] sm:px-4 sm:py-16">
        <div className="mx-auto grid w-full max-w-[1728px] grid-cols-2 items-center gap-16 sm:grid-cols-1 sm:gap-10">
          <div className="flex flex-col items-start gap-12 sm:gap-8">
            <h2 className="font-semibold text-[48px] text-white leading-[1.3] sm:text-3xl">{t('aboutTitle')}</h2>
            <div className="space-y-5 font-normal text-base text-white/64 leading-[1.8] sm:text-sm">
              <p>{t('aboutBody1')}</p>
              <p>{t('aboutBody2')}</p>
              <p>{t('aboutBody3')}</p>
            </div>
            <LandingCta onClick={handleApply}>{ctaLabel}</LandingCta>
          </div>
          <div className="relative aspect-square overflow-hidden rounded-[36px] bg-[#171717] sm:rounded-[24px]">
            <img src={LANDING_ASSETS.collage} alt="" aria-hidden="true" className="size-full object-cover" />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden px-[120px] py-[140px] sm:px-4 sm:py-16">
        <div className="mx-auto flex w-full max-w-[1680px] flex-col items-center gap-[68px] sm:gap-10">
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="font-semibold text-[48px] text-white leading-[1.3] sm:text-3xl">{t('benefitsTitle')}</h2>
            <p className="font-normal text-base text-white/56 leading-[1.6] sm:text-sm">{t('benefitsSubtitle')}</p>
          </div>
          <div className="grid w-full grid-cols-3 gap-6 sm:grid-cols-1">
            {BENEFIT_KEYS.slice(0, 3).map((item) => (
              <article
                key={item.title}
                className="flex flex-col gap-4 rounded-[32px] bg-gradient-to-b from-[#2a2d31] to-[#17191b] p-10 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)] sm:rounded-[24px] sm:p-6"
              >
                <p className="font-medium text-[#668cff] text-[32px] leading-none sm:text-3xl">{item.index}</p>
                <h3 className="font-medium text-[22px] text-white leading-[1.4] sm:text-xl">{t(item.title)}</h3>
                <p className="font-normal text-base text-white/56 leading-[1.8] sm:text-sm">{t(item.body)}</p>
              </article>
            ))}
          </div>
          <div className="grid w-full grid-cols-2 gap-6 sm:grid-cols-1">
            {BENEFIT_KEYS.slice(3).map((item) => (
              <article
                key={item.title}
                className="flex flex-col gap-4 rounded-[32px] bg-gradient-to-b from-[#2a2d31] to-[#17191b] p-10 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)] sm:rounded-[24px] sm:p-6"
              >
                <p className="font-medium text-[#668cff] text-[32px] leading-none sm:text-3xl">{item.index}</p>
                <h3 className="font-medium text-[22px] text-white leading-[1.4] sm:text-xl">{t(item.title)}</h3>
                <p className="font-normal text-base text-white/56 leading-[1.8] sm:text-sm">{t(item.body)}</p>
              </article>
            ))}
          </div>
          <LandingCta onClick={handleApply}>{ctaLabel}</LandingCta>
        </div>
      </section>

      <section className="relative overflow-hidden px-[120px] py-[140px] sm:px-4 sm:py-16">
        <div className="mx-auto flex w-full max-w-[1680px] flex-col items-center gap-[68px] sm:gap-10">
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="font-semibold text-[48px] text-white leading-[1.3] sm:text-3xl">{t('creatorsTitle')}</h2>
            <p className="font-normal text-base text-white/56 leading-[1.6] sm:text-sm">{t('creatorsSubtitle')}</p>
          </div>
          <div className="flex w-full flex-col gap-6">
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-1">
              <article className="flex gap-10 rounded-[36px] bg-white/8 p-12 sm:p-5 md:flex-col md:items-center md:gap-6 md:rounded-[24px] md:p-6 lg:gap-8 lg:p-8">
                <div className="relative h-[340px] w-[260px] shrink-0 overflow-hidden rounded-[24px] sm:h-[180px] sm:w-[138px] md:mx-auto md:h-[200px] md:w-[152px] lg:h-[240px] lg:w-[184px]">
                  <img
                    src={LANDING_ASSETS.creatorRising}
                    alt=""
                    aria-hidden="true"
                    className="size-full object-cover"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-6 sm:gap-3 md:w-full md:items-center md:gap-4 md:text-center">
                  <span className="inline-flex w-fit rounded-full bg-white/8 px-[18px] py-2 font-medium text-[14px] text-white/90">
                    {t('risingTag')}
                  </span>
                  <h3 className="font-medium text-[32px] text-white leading-[1.4] sm:text-2xl md:text-[28px] lg:text-[32px]">
                    {t('risingTitle')}
                  </h3>
                  <p className="font-normal text-base text-white/72 leading-[1.8] sm:text-sm md:text-base lg:text-base">
                    {t('risingBody')}
                  </p>
                  <p className="font-normal text-base text-white/56 leading-[1.8] sm:text-xs md:text-sm">
                    {t('risingHint')}
                  </p>
                </div>
              </article>
              <article className="flex gap-10 rounded-[36px] bg-white/8 p-12 sm:p-5 md:flex-col md:items-center md:gap-6 md:rounded-[24px] md:p-6 lg:gap-8 lg:p-8">
                <div className="relative h-[340px] w-[260px] shrink-0 overflow-hidden rounded-[24px] sm:h-[180px] sm:w-[138px] md:mx-auto md:h-[200px] md:w-[152px] lg:h-[240px] lg:w-[184px]">
                  <img
                    src={LANDING_ASSETS.creatorArtist}
                    alt=""
                    aria-hidden="true"
                    className="size-full object-cover"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-6 sm:gap-3 md:w-full md:items-center md:gap-4 md:text-center">
                  <span className="inline-flex w-fit rounded-full bg-white/8 px-[18px] py-2 font-medium text-[14px] text-white/90">
                    {t('artistTag')}
                  </span>
                  <h3 className="font-medium text-[32px] text-white leading-[1.4] sm:text-2xl md:text-[28px] lg:text-[32px]">
                    {t('artistTitle')}
                  </h3>
                  <p className="font-normal text-base text-white/72 leading-[1.8] sm:text-sm md:text-base lg:text-base">
                    {t('artistBody')}
                  </p>
                  <p className="font-normal text-base text-white/56 leading-[1.8] sm:text-xs md:text-sm">
                    {t('artistHint')}
                  </p>
                </div>
              </article>
            </div>
            <article className="rounded-[36px] bg-white/8 p-14 sm:rounded-[24px] sm:p-6">
              <h3 className="font-medium text-[24px] text-white leading-[1.4] sm:text-2xl">{t('criteriaTitle')}</h3>
              <ul className="mt-8 space-y-5 sm:mt-6 sm:space-y-4">
                {CRITERIA_KEYS.map((key) => (
                  <li key={key} className="flex items-center gap-4">
                    <span className="size-2.5 shrink-0 rounded-full bg-[#668cff]" aria-hidden="true" />
                    <span className="font-normal text-base text-white/56 leading-[1.7] sm:text-sm">{t(key)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 font-normal text-base text-white/56 leading-[1.8] sm:mt-6 sm:text-sm">
                {t('criteriaFooter')}
              </p>
            </article>
          </div>
          <LandingCta onClick={handleApply}>{ctaLabel}</LandingCta>
        </div>
      </section>

      <section className="relative overflow-hidden px-[120px] py-[120px] sm:px-4 sm:py-16">
        <div className="mx-auto flex w-full max-w-[1680px] flex-col items-center gap-[68px] sm:gap-10">
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="font-semibold text-[48px] text-white leading-[1.3] sm:text-3xl">{t('howTitle')}</h2>
            <p className="font-normal text-base text-white/56 leading-[1.6] sm:text-sm">{t('howSubtitle')}</p>
          </div>
          <div className="grid w-full grid-cols-3 gap-6 sm:grid-cols-1">
            {STEP_KEYS.map((item) => (
              <article key={item.title} className="flex flex-col items-center gap-9 text-center">
                <div className="relative aspect-[544/440] w-full overflow-hidden rounded-[36px] bg-[#171717] sm:rounded-[24px]">
                  <img src={item.image} alt="" aria-hidden="true" className="size-full object-cover" />
                </div>
                <div className="flex flex-col items-center gap-6">
                  <div className="flex flex-col items-center gap-4">
                    <span className="rounded-8 bg-white/8 px-3 py-1.5 font-medium text-sm text-white sm:text-sm">
                      {t(item.step)}
                    </span>
                    <h3 className="font-medium text-[24px] text-white leading-none sm:text-2xl">{t(item.title)}</h3>
                  </div>
                  <p className="font-normal text-base text-white/56 leading-[1.8] sm:text-sm">{t(item.body)}</p>
                </div>
              </article>
            ))}
          </div>
          <LandingCta onClick={handleApply}>{ctaLabel}</LandingCta>
        </div>
      </section>

      <LandingFaq />

      {dialogView && (
        <ApplicationDialog
          open
          view={dialogView}
          onClose={() => setDialogView(null)}
          status={
            dialogView === 'application'
              ? undefined
              : {
                  title: dialogView === 'reviewing' ? t('reviewingTitle') : t('rejectedTitle'),
                  body:
                    dialogView === 'reviewing'
                      ? t('reviewingBody')
                      : `${t('rejectedThanks')}\n${t('rejectedBody')}\n${t('rejectedReason')}${my?.application?.reason ? ` ${my.application.reason}` : ` ${t('rejectedReasonFallback')}`}`,
                  action: dialogView === 'reviewing' ? t('back') : t('reapply'),
                  onAction: () => setDialogView(dialogView === 'rejected' ? 'application' : null),
                }
          }
          onSubmitted={() => {
            setDialogView('reviewing');
            void refresh({ force: true });
          }}
        />
      )}
    </>
  );
}
